<script setup lang="ts">
import IconEye from '@/components/icons/iconEronor/IconEye.vue'
import IconEyeOff from '@/components/icons/iconEronor/IconEyeOff.vue'
import {
    alertMessageEmptyHappy,
    alertMessageHappy,
    showAlertMessageHappy
} from '@/happyTheme/event/dashboard/alert/AlertMessageHappy'
import {
    alertDeleteCustomerMessageHappy,
    alertDeleteCustomerMessageHappyEmpty
} from '@/happyTheme/event/dashboard/user/alertDeleteCustomerMessageHappy'
import { requestDeleteCustomer } from '@/happyTheme/event/dashboard/user/requestDeleteCustomer'
import { TimeOutClass } from '@/happyTheme/services/timeout/TimeOutClass'
import { UserService } from '@/services/UserService'
import customer from '@/storage/customer'
import { tokenSessionId } from '@/storage/setting/tokenSessionId'
import stores from '@/storage/stores'
import { encryptPassword } from '@/utils/account/encryptPassword'
import { ref, watch } from 'vue'
import { useRouter } from 'vue-router'

const isVisible = ref(false)
const password = ref<string | null>(null)
const showPassword = ref(false)
const router = useRouter()

function cancelRequestDeleteCustomer() {
    isVisible.value = false

    const timeOutClass = TimeOutClass.getInstance()
    const idTimeout = setTimeout(() => {
        clearTimeout(idTimeout)
        requestDeleteCustomer.value = false
    }, 300)
    timeOutClass.setIdTimeout(idTimeout)
}

async function useDeleteCustomer() {
    if (!password.value || password.value.length === 0) {
        alertMessageHappy.value = {
            title: 'Erreur lors de la suppression du compte',
            class: 'error',
            message: 'Veuillez rentrer votre mot de passe',
            errors: []
        }
        const timeOutClass = TimeOutClass.getInstance()
        const showTimeOut = setTimeout(() => {
            showAlertMessageHappy.value = true
        }, 100)
        timeOutClass.setTimeoutWithKey('delete', showTimeOut)

        const idTimeOut = setTimeout(() => {
            showAlertMessageHappy.value = false
            setTimeout(() => {
                alertMessageHappy.value = alertMessageEmptyHappy
            }, 500)
        }, 2000)

        timeOutClass.setIdTimeout(idTimeOut)

        return
    }

    const store = stores.getStoresData()
    const currentCustomer = customer.getCustomerData()

    if (
        (!currentCustomer || !currentCustomer.customer_id) &&
        (!store || !store.store_id)
    ) {
        return
    }
    const passwordEncrypted = await encryptPassword(password.value)

    const storeId = store.store_id

    const userService = new UserService()
    userService.deleteCustomer(
        {
            token_session_id: tokenSessionId.value.token_session_id,
            store_id: storeId,
            customer_id: currentCustomer.customer_id,
            password: passwordEncrypted
        },
        router
    )
}

watch(
    () => requestDeleteCustomer.value,
    (newValue) => {
        if (newValue) {
            setTimeout(() => {
                isVisible.value = true
            }, 10)
        } else {
            isVisible.value = false
        }
    },
    { immediate: true }
)

watch(
    () => alertDeleteCustomerMessageHappy.value,
    (newValue) => {
        if (newValue && newValue.message) {
            setTimeout(() => {
                isVisible.value = true
            }, 10)
        } else {
            isVisible.value = false
        }
    },
    { immediate: true }
)
</script>

<template>
    <section class="section-alert-delete-customer-happy">
        <section class="section-alert-delete-customer-overlay"></section>

        <Transition
            name="transition-information-user-happy"
            enter-active-class="transition-all duration-300 ease-out"
            enter-from-class="translate-y-[-100%] opacity-0"
            enter-to-class="translate-y-0 opacity-100"
            leave-active-class="transition-all duration-300 ease-out"
            leave-from-class="translate-y-0 opacity-100"
            leave-to-class="translate-y-[-100%] opacity-0"
        >
            <section class="section-alert-delete-customer-content-happy" v-if="isVisible">
                <div class="div-title-alert-delete-customer-happy">
                    <h3>Demande de suppression de compte</h3>
                </div>

                <section class="section-content-alert-delete-customer-happy">
                    <div class="div-message-alert-delete-customer-happy">
                        <p class="p-message-alert-delete-customer-happy">
                            Si vous souhaité supprimer votre compte, veuillez rentrer
                            votre mot de passe !
                        </p>
                    </div>

                    <form class="div-input-password-alert-delete-customer-happy">
                        <label
                            class="label-password-alert-delete-customer-happy"
                            for="password"
                            >Mot de passe</label
                        >
                        <div class="div-input-alert-delete-customer-happy">
                            <input
                                class="input-password-alert-delete-customer-happy"
                                :type="showPassword ? 'text' : 'password'"
                                id="password"
                                v-model="password"
                                autocomplete="current-password"
                            />
                            <span
                                class="eye-icon-happy"
                                @click="showPassword = !showPassword"
                            >
                                <template v-if="showPassword">
                                    <IconEye />
                                </template>
                                <template v-else>
                                    <IconEyeOff />
                                </template>
                            </span>
                        </div>
                    </form>

                    <div class="div-button-alert-delete-customer-happy">
                        <button
                            class="div-alert-delete-customer-button-cancel-happy"
                            @click="cancelRequestDeleteCustomer()"
                        >
                            <p class="p-alert-delete-customer-button-text-happy">
                                Annuler
                            </p>
                        </button>
                        <button
                            class="div-alert-delete-customer-button-confirm-happy"
                            @click="useDeleteCustomer()"
                        >
                            <p class="p-alert-delete-customer-button-text-happy">
                                Confirmer
                            </p>
                        </button>
                    </div>
                </section>
            </section>
        </Transition>
    </section>
</template>

<style scoped>
.section-alert-delete-customer-happy {
    @apply fixed flex top-0 left-0 w-full h-full justify-center  z-30;
}

.section-alert-delete-customer-overlay {
    @apply absolute w-full h-full bg-transparent;
}

.section-alert-delete-customer-content-happy {
    @apply z-40 absolute w-[20rem] text-bg-happy-light flex flex-col justify-center items-center mt-44 rounded-md transition-all duration-300 ease-out h-auto overflow-hidden;
}

.div-title-alert-delete-customer-happy {
    @apply w-full p-4 bg-header-red-happy font-semibold text-wrap text-start;
}

.section-content-alert-delete-customer-happy {
    @apply w-full flex-col flex justify-between items-center p-4 bg-border-red-happy-light gap-4;
}
.p-message-alert-delete-customer-happy {
    @apply text-center;
}

.div-input-password-alert-delete-customer-happy {
    @apply w-full flex flex-col items-center;
}
.label-password-alert-delete-customer-happy {
    @apply font-medium;
}
.div-input-alert-delete-customer-happy {
    @apply w-full flex flex-col justify-center gap-2 relative;
}
.input-password-alert-delete-customer-happy {
    @apply w-full border-[1.5px] border-border-gray-happy-light text-center text-text-dark-happy-light rounded-lg px-2 py-1 outline-none;
}
.input-password-alert-delete-customer-happy:focus {
    @apply border-text-dark-happy-light shadow-sm;
}
.eye-icon-happy {
    @apply absolute right-2 cursor-pointer;
}
.div-button-alert-delete-customer-happy {
    @apply w-full flex justify-around gap-3;
}
.div-alert-delete-customer-button-cancel-happy {
    @apply bg-text-dark-happy-light text-bg-happy-light text-center border border-text-dark-happy-light rounded px-2 py-0.5 shadow-sm shadow-bg-happy-light;
}
.div-alert-delete-customer-button-cancel-happy:active,
.div-alert-delete-customer-button-cancel-happy:focus {
    @apply bg-border-gray-happy-light;
}

.div-alert-delete-customer-button-confirm-happy {
    @apply border border-text-dark-happy-light text-bg-happy-light px-2 py-0.5 rounded text-center  shadow-sm shadow-text-dark-happy-light;
}
.div-alert-delete-customer-button-confirm-happy:active,
.div-alert-delete-customer-button-confirm-happy:focus {
    @apply bg-reddark-eronor;
}

@media (hover: hover) and (pointer: fine) {
    .div-alert-delete-customer-button-cancel-happy:hover {
        @apply bg-border-gray-happy-light;
    }
    .div-alert-delete-customer-button-confirm-happy:hover {
        @apply bg-reddark-eronor;
    }
}
</style>
